import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import type { QuizSolveResult, BaseQuizData } from '../../../types/legacyQuiz'

const LINE_COLOR_DEFAULT = '#55C3FF'
const LINE_COLOR_TRUE = '#3BD0AE'
const LINE_COLOR_FALSE = '#FF675E'
const LINE_COLOR_PAIRED = '#D0D0D0'

export interface PairingQuizData extends BaseQuizData {
  type: 'pairing'
}

export type PairAnswer = {
  leftIndex: number
  rightIndex: number
  rightKey: string
} | null

export type SelectedSide = {
  pos: 'left' | 'right'
  index: number
} | null

export interface LinePosition {
  leftIdx: number
  rightIdx: number
  x1: number
  y1: number
  x2: number
  y2: number
}

export interface UsePairingQuizOptions {
  quizId: string | number
  quiz: PairingQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  onAnswerChange?: (answers: string[]) => void
}

export function usePairingQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
}: UsePairingQuizOptions) {
  const [startTime] = useState(() => Date.now())
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false)
  const [hoveredPos, setHoveredPos] = useState<SelectedSide>(null)

  const isSolved = solveResult !== null && solveResult !== undefined
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers
  const pairs = quiz.pairs

  const containerRef = useRef<HTMLDivElement>(null)
  const leftRefs = useRef<(HTMLDivElement | null)[]>([])
  const rightRefs = useRef<(HTMLDivElement | null)[]>([])

  const computedInitialPairings = useMemo((): (PairAnswer | null)[] => {
    const empty: (PairAnswer | null)[] = new Array(pairs.left.length).fill(null)
    const answers = initialAnswersProp ?? (isViewSolved && solveResult?.correctAnswers ? solveResult.correctAnswers : null)
    if (!answers || answers.length === 0) return empty
    return pairs.left.map((_, leftIdx) => {
      const rightKey = answers[leftIdx]
      if (!rightKey) return null
      const rightIndex = pairs.right.findIndex(r => r.key === rightKey)
      if (rightIndex === -1) return null
      return { leftIndex: leftIdx, rightIndex, rightKey }
    })
  }, [pairs.left, pairs.right, initialAnswersProp, isViewSolved, solveResult?.correctAnswers])

  const [pairings, setPairings] = useState<(PairAnswer | null)[]>(computedInitialPairings)
  const [selected, setSelected] = useState<SelectedSide>(null)
  const [lines, setLines] = useState<LinePosition[]>([])
  const [previewLine, setPreviewLine] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null)

  useEffect(() => {
    setPairings(prev => {
      if (prev.length === computedInitialPairings.length) {
        const same = prev.every((p, i) => {
          const c = computedInitialPairings[i]
          if (p === c) return true
          if (!p || !c) return false
          return p.rightKey === c.rightKey && p.rightIndex === c.rightIndex && p.leftIndex === c.leftIndex
        })
        if (same) return prev
      }
      return computedInitialPairings
    })
  }, [computedInitialPairings])

  const correctPairings = useMemo((): (PairAnswer | null)[] => {
    if (!solveResult?.correctAnswers) return pairings
    return pairs.left.map((_, leftIdx) => {
      const rightKey = solveResult.correctAnswers[leftIdx]
      if (!rightKey) return null
      const rightIndex = pairs.right.findIndex(r => r.key === rightKey)
      if (rightIndex === -1) return null
      return { leftIndex: leftIdx, rightIndex, rightKey }
    })
  }, [solveResult?.correctAnswers, pairs.left, pairs.right, pairings])

  const displayPairings = shouldShowCorrectAnswers ? correctPairings : pairings

  const lastEmittedAnswersRef = useRef<string[] | null>(null)
  useEffect(() => {
    const answers = pairings.map(p => p?.rightKey || '')
    const last = lastEmittedAnswersRef.current
    if (last && last.length === answers.length && last.every((v, i) => v === answers[i])) {
      return
    }
    lastEmittedAnswersRef.current = answers
    onAnswerChange?.(answers)
  }, [pairings, onAnswerChange])

  const recalcLines = useCallback(() => {
    if (!containerRef.current) return
    const containerRect = containerRef.current.getBoundingClientRect()
    const newLines: LinePosition[] = []
    displayPairings.forEach((pair, leftIdx) => {
      if (!pair) return
      const leftEl = leftRefs.current[leftIdx]
      const rightEl = rightRefs.current[pair.rightIndex]
      if (!leftEl || !rightEl) return
      const leftRect = leftEl.getBoundingClientRect()
      const rightRect = rightEl.getBoundingClientRect()
      newLines.push({
        leftIdx,
        rightIdx: pair.rightIndex,
        x1: leftRect.right - containerRect.left,
        y1: leftRect.top + leftRect.height / 2 - containerRect.top,
        x2: rightRect.left - containerRect.left,
        y2: rightRect.top + rightRect.height / 2 - containerRect.top,
      })
    })
    setLines(newLines)
  }, [displayPairings])

  useEffect(() => {
    const rafId = requestAnimationFrame(recalcLines)
    return () => cancelAnimationFrame(rafId)
  }, [recalcLines])

  useEffect(() => {
    let rafId: number | undefined
    const handleResize = () => {
      if (rafId != null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(recalcLines)
    }
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(handleResize)
    if (containerRef.current) observer?.observe(containerRef.current)
    window.addEventListener('resize', handleResize)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', handleResize)
      if (rafId != null) cancelAnimationFrame(rafId)
    }
  }, [recalcLines])

  const createPair = useCallback((leftIdx: number, rightIdx: number) => {
    setPairings(prev => {
      const next = [...prev]
      for (let i = 0; i < next.length; i++) {
        if (next[i]?.rightIndex === rightIdx) {
          next[i] = null
        }
      }
      next[leftIdx] = {
        leftIndex: leftIdx,
        rightIndex: rightIdx,
        rightKey: pairs.right[rightIdx].key,
      }
      return next
    })
    setSelected(null)
  }, [pairs.right])

  const handleSelect = useCallback((pos: 'left' | 'right', index: number) => {
    if (isSolved || isViewSolved) return
    setSelected(prev => {
      if (prev?.pos === pos && prev?.index === index) {
        return null
      }
      if (prev?.pos === pos) {
        return { pos, index }
      }
      if (prev) {
        const leftIdx = pos === 'left' ? index : prev.index
        const rightIdx = pos === 'right' ? index : prev.index
        setTimeout(() => createPair(leftIdx, rightIdx), 0)
        return null
      }
      return { pos, index }
    })
  }, [isSolved, isViewSolved, createPair])

  const handleClearPairByLeft = useCallback((leftIdx: number) => {
    if (isSolved || isViewSolved) return
    setPairings(prev => {
      const next = [...prev]
      next[leftIdx] = null
      return next
    })
    setSelected(null)
  }, [isSolved, isViewSolved])

  const handleClearPairByRight = useCallback((rightIdx: number) => {
    if (isSolved || isViewSolved) return
    setPairings(prev => {
      const next = [...prev]
      const leftIdx = next.findIndex(p => p?.rightIndex === rightIdx)
      if (leftIdx !== -1) next[leftIdx] = null
      return next
    })
    setSelected(null)
  }, [isSolved, isViewSolved])

  const canSubmit = pairings.some(p => p !== null)

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return
    const elapsedTime = Date.now() - startTime
    const answers = pairings.map(p => p?.rightKey || '')
    onSolve?.(quizId, answers, quiz.vars, elapsedTime)
  }, [canSubmit, isSolved, startTime, pairings, onSolve, quizId, quiz.vars])

  useEffect(() => {
    if (isSolved || !selected || !hoveredPos || hoveredPos.pos === selected.pos || !containerRef.current) {
      setPreviewLine(null)
      return
    }
    const containerRect = containerRef.current.getBoundingClientRect()
    const leftIdx = selected.pos === 'left' ? selected.index : hoveredPos.index
    const rightIdx = selected.pos === 'right' ? selected.index : hoveredPos.index
    const leftEl = leftRefs.current[leftIdx]
    const rightEl = rightRefs.current[rightIdx]
    if (!leftEl || !rightEl) {
      setPreviewLine(null)
      return
    }
    const leftRect = leftEl.getBoundingClientRect()
    const rightRect = rightEl.getBoundingClientRect()
    setPreviewLine({
      x1: leftRect.right - containerRect.left,
      y1: leftRect.top + leftRect.height / 2 - containerRect.top,
      x2: rightRect.left - containerRect.left,
      y2: rightRect.top + rightRect.height / 2 - containerRect.top,
    })
  }, [isSolved, selected, hoveredPos])

  const hasAnyAnswer = pairings.some(p => p !== null)

  const getLineColor = useCallback((leftIdx: number) => {
    if (shouldShowCorrectAnswers) return LINE_COLOR_TRUE
    if (isSolved) {
      return solveResult?.corrects?.[leftIdx] === true ? LINE_COLOR_TRUE : LINE_COLOR_FALSE
    }
    if (selected) return LINE_COLOR_PAIRED
    return LINE_COLOR_DEFAULT
  }, [isSolved, shouldShowCorrectAnswers, solveResult?.corrects, selected])

  return {
    pairings,
    displayPairings,
    selected,
    lines,
    previewLine,
    hoveredPos,
    setHoveredPos,
    hasAnyAnswer,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    pairs,
    containerRef,
    leftRefs,
    rightRefs,
    handleSelect,
    handleClearPairByLeft,
    handleClearPairByRight,
    handleSubmit,
    recalcLines,
    getLineColor,
    quiz,
    quizId,
    solveResult,
    isViewSolved,
  }
}
